import React, { useState, useMemo } from 'react';
import { useUserCalendarIntegrations } from '../hooks';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

export interface CalendarFilters {
  integrations: string[];
  statuses: string[];
  types: string[];
  selectedDate?: Date;
}

interface IntegrationListItem {
  id: string;
  name: string;
  color: string;
  isLocal: boolean;
  status?: string;
  authFailureReason?: string;
}

interface CalendarSidebarProps {
  filters: CalendarFilters;
  onFiltersChange: (filters: CalendarFilters) => void;
  onDateSelect: (date: Date) => void;
  currentDate: Date;
  onSyncCalendars?: () => void;
  onAvailabilitySettings?: () => void;
  /**
   * `'full'` (default): sidebar completa (mini calendário + 3 grupos de filtro +
   * ações). `'filters'`: SÓ os 3 grupos (Meus calendários/Status/Tipo), sem mini
   * calendário nem ações — para o bottom-sheet de filtros no mobile (#130).
   */
  variant?: 'full' | 'filters';
}

// Helper function to get provider color
const getProviderColor = (providerId: string): string => {
  // Normalizar para aceitar tanto hífen quanto underscore
  const normalizedId = providerId.toLowerCase().replace(/-/g, '_');

  switch (normalizedId) {
    case 'google_calendar':
      return '#EA4335';
    case 'outlook_calendar':
      return '#0078D4';
    case 'icloud_calendar':
    case 'apple_calendar':
      return '#000000';
    default:
      return '#8b5cf6'; // Purple fallback
  }
};

// Helper function to get provider display name
const getProviderName = (providerId: string): string => {
  // Normalizar para aceitar tanto hífen quanto underscore
  const normalizedId = providerId.toLowerCase().replace(/-/g, '_');

  switch (normalizedId) {
    case 'google_calendar':
      return 'Google Calendar';
    case 'outlook_calendar':
      return 'Outlook';
    case 'icloud_calendar':
    case 'apple_calendar':
      return 'iCloud';
    default:
      return providerId;
  }
};

export const STATUSES = [
  { id: 'confirmed', name: 'Confirmado', color: '#10B981' },
  { id: 'tentative', name: 'Tentativo', color: '#F59E0B' },
  { id: 'cancelled', name: 'Cancelado', color: '#EF4444' },
];

export const EVENT_TYPES: { id: string; name: string; icon: IconName }[] = [
  { id: 'meeting', name: 'Reunião', icon: 'calendar' },
  { id: 'task', name: 'Tarefa', icon: 'check-circle' },
  { id: 'reminder', name: 'Lembrete', icon: 'circle' },
  { id: 'follow_up', name: 'Retorno', icon: 'corner-up-left' },
  { id: 'activity', name: 'Atividades', icon: 'activity' },
  { id: 'other', name: 'Outro', icon: 'grid' },
];

export const CalendarSidebar: React.FC<CalendarSidebarProps> = ({
  filters,
  onFiltersChange,
  onDateSelect,
  currentDate,
  onSyncCalendars,
  onAvailabilitySettings,
  variant = 'full',
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Fetch user calendar integrations
  const { data: userCalendars = [], isLoading: isLoadingCalendars } = useUserCalendarIntegrations();

  // Build dynamic integrations list
  const INTEGRATIONS = useMemo((): IntegrationListItem[] => {
    const integrations: IntegrationListItem[] = [
      { id: 'local', name: 'Calendário Local', color: '#3B82F6', isLocal: true, status: 'active' }
    ];

    // Add user calendar integrations (active, auth_failed, disconnected)
    userCalendars.forEach(calendar => {
      // Show active calendars and failed integrations (for reconnection)
      if (calendar.status === 'active' || calendar.status === 'auth_failed' || calendar.status === 'disconnected') {
        integrations.push({
          id: calendar.id,
          name: getProviderName(calendar.providerId),
          color: getProviderColor(calendar.providerId),
          isLocal: false,
          status: calendar.status,
          authFailureReason: calendar.authFailureReason
        });
      }
    });

    return integrations;
  }, [userCalendars]);

  const hasFailedCalendar = userCalendars.some(cal => cal.status !== 'active');
  const noCalendarsConnected = userCalendars.length === 0 || userCalendars.every(cal => cal.status === 'active');

  // Mini calendar helpers
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (number | null)[] = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDayClick = (day: number) => {
    const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    onDateSelect(selectedDate);
  };

  const isToday = (day: number | null) => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day: number | null) => {
    if (!day) return false;
    return (
      day === currentDate.getDate() &&
      currentMonth.getMonth() === currentDate.getMonth() &&
      currentMonth.getFullYear() === currentDate.getFullYear()
    );
  };

  const toggleIntegration = (integrationId: string) => {
    const newIntegrations = filters.integrations.includes(integrationId)
      ? filters.integrations.filter(id => id !== integrationId)
      : [...filters.integrations, integrationId];

    onFiltersChange({ ...filters, integrations: newIntegrations });
  };

  const toggleStatus = (statusId: string) => {
    const newStatuses = filters.statuses.includes(statusId)
      ? filters.statuses.filter(id => id !== statusId)
      : [...filters.statuses, statusId];

    onFiltersChange({ ...filters, statuses: newStatuses });
  };

  const toggleType = (typeId: string) => {
    const newTypes = filters.types.includes(typeId)
      ? filters.types.filter(id => id !== typeId)
      : [...filters.types, typeId];

    onFiltersChange({ ...filters, types: newTypes });
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

  const groupHeaderStyle: React.CSSProperties = { color: 'var(--theme-muted-text)' };

  return (
    <div
      className={variant === 'filters' ? 'w-full' : 'w-[270px] flex-shrink-0 h-full overflow-y-auto border-r'}
      style={variant === 'filters' ? undefined : {
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid="calendar-sidebar"
    >
      {/* Mini Calendar */}
      {variant !== 'filters' && (
      <div className="p-3.5 pb-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold capitalize" style={{ color: 'var(--theme-card-text)' }}>
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <div className="flex gap-1">
            <button
              onClick={handlePreviousMonth}
              className="inline-flex items-center justify-center w-6 h-6 rounded-md transition-colors hover:bg-[var(--theme-button-ghost-hover)]"
              style={{ color: 'var(--theme-muted-text)' }}
              title="Mês anterior"
              data-testid="calendar-mini-prev"
            >
              <Icon name="chevron-left" className="w-4 h-4" />
            </button>
            <button
              onClick={handleNextMonth}
              className="inline-flex items-center justify-center w-6 h-6 rounded-md transition-colors hover:bg-[var(--theme-button-ghost-hover)]"
              style={{ color: 'var(--theme-muted-text)' }}
              title="Próximo mês"
              data-testid="calendar-mini-next"
            >
              <Icon name="chevron-right" className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Week days header */}
        <div className="grid grid-cols-7 mb-0.5">
          {weekDays.map((day, i) => (
            <div
              key={i}
              className="h-5 flex items-center justify-center text-[10px]"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-px">
          {getDaysInMonth(currentMonth).map((day, index) => {
            const today = isToday(day);
            const selected = !today && isSelected(day);
            return (
              <button
                key={index}
                onClick={() => day && handleDayClick(day)}
                disabled={!day}
                className={`h-7 rounded-md text-xs tabular-nums inline-flex items-center justify-center transition-colors ${day ? 'cursor-pointer hover:bg-[var(--theme-button-ghost-hover)]' : 'cursor-default'} ${today ? 'font-bold' : ''}`}
                style={{
                  color: today ? '#FFFFFF' : day ? 'var(--theme-card-text)' : 'transparent',
                  backgroundColor: today ? 'var(--theme-primary)' : 'transparent',
                  boxShadow: selected ? 'inset 0 0 0 1.5px var(--theme-primary)' : undefined,
                }}
                data-testid={day ? `calendar-mini-day-${day}` : undefined}
              >
                {day || ''}
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* My Calendars */}
      <div className="px-3.5 py-3 border-t" style={{ borderColor: 'var(--theme-card-border)' }}>
        <h5 className="text-[11px] font-bold uppercase tracking-wide mb-2" style={groupHeaderStyle}>
          Meus Calendários
        </h5>
        <div className="flex flex-col gap-0.5">
          {isLoadingCalendars && (
            <div className="flex items-center gap-2 p-2 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
              <div
                className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: 'var(--theme-primary)' }}
              />
              <span>Carregando calendários...</span>
            </div>
          )}
          {INTEGRATIONS.map((integration) => {
            const isActive = filters.integrations.includes(integration.id);
            const hasFailed = integration.status === 'auth_failed' || integration.status === 'disconnected';

            return (
              <button
                key={integration.id}
                onClick={() => !hasFailed && toggleIntegration(integration.id)}
                disabled={hasFailed}
                className={`w-full flex items-center gap-2.5 px-1.5 py-1.5 rounded-md text-left text-sm transition-colors ${hasFailed ? 'cursor-not-allowed opacity-60' : 'hover:bg-[var(--theme-button-ghost-hover)]'}`}
                style={{ color: isActive ? 'var(--theme-card-text)' : 'var(--theme-muted-text)' }}
                title={hasFailed ? (integration.authFailureReason || 'Autenticação falhou') : integration.name}
                data-testid={`calendar-filter-integration-${integration.id}`}
              >
                <span
                  className="w-3 h-3 rounded flex-shrink-0"
                  style={{ backgroundColor: integration.color }}
                />
                <span className="flex-1 truncate">{integration.name}</span>
                {hasFailed && (
                  <Icon name="alert-triangle" className="h-4 w-4 flex-shrink-0" style={{ color: '#f59e0b' }} />
                )}
                {isActive && !hasFailed && (
                  <Icon name="check" className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--theme-primary)' }} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter by Status */}
      <div className="px-3.5 py-3 border-t" style={{ borderColor: 'var(--theme-card-border)' }}>
        <h5 className="text-[11px] font-bold uppercase tracking-wide mb-2" style={groupHeaderStyle}>
          Status
        </h5>
        <div className="flex flex-col gap-0.5">
          {STATUSES.map((status) => {
            const isActive = filters.statuses.includes(status.id);
            return (
              <button
                key={status.id}
                onClick={() => toggleStatus(status.id)}
                className="w-full flex items-center gap-2.5 px-1.5 py-1.5 rounded-md text-left text-sm transition-colors hover:bg-[var(--theme-button-ghost-hover)]"
                style={{ color: isActive ? 'var(--theme-card-text)' : 'var(--theme-muted-text)' }}
                data-testid={`calendar-filter-status-${status.id}`}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: status.color }}
                />
                <span className="flex-1 truncate">{status.name}</span>
                {isActive && (
                  <Icon name="check" className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--theme-primary)' }} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter by Type */}
      <div className="px-3.5 py-3 border-t" style={{ borderColor: 'var(--theme-card-border)' }}>
        <h5 className="text-[11px] font-bold uppercase tracking-wide mb-2" style={groupHeaderStyle}>
          Tipo de Evento
        </h5>
        <div className="flex flex-col gap-0.5">
          {EVENT_TYPES.map((type) => {
            const isActive = filters.types.includes(type.id);
            return (
              <button
                key={type.id}
                onClick={() => toggleType(type.id)}
                className="w-full flex items-center gap-2.5 px-1.5 py-1.5 rounded-md text-left text-sm transition-colors hover:bg-[var(--theme-button-ghost-hover)]"
                style={{ color: isActive ? 'var(--theme-card-text)' : 'var(--theme-muted-text)' }}
                data-testid={`calendar-filter-type-${type.id}`}
              >
                <Icon name={type.icon} className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
                <span className="flex-1 truncate">{type.name}</span>
                {isActive && (
                  <Icon name="check" className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--theme-primary)' }} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      {variant !== 'filters' && (
      <div className="px-2 py-2 border-t flex flex-col gap-0.5" style={{ borderColor: 'var(--theme-card-border)' }}>
        {onAvailabilitySettings && (
          <button
            onClick={onAvailabilitySettings}
            className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left text-sm font-semibold transition-colors hover:bg-[var(--theme-button-ghost-hover)]"
            style={{ color: 'var(--theme-primary)' }}
            data-testid="calendar-availability-settings"
          >
            <Icon name="settings" className="w-4 h-4" />
            Configurações
          </button>
        )}
        {onSyncCalendars && (
          <button
            onClick={onSyncCalendars}
            className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left text-sm font-semibold transition-colors hover:bg-[var(--theme-button-ghost-hover)]"
            style={{ color: hasFailedCalendar ? '#b45309' : 'var(--theme-primary)' }}
            data-testid="calendar-sync-integrations"
          >
            <Icon name={hasFailedCalendar ? 'alert-triangle' : 'tag'} className="w-4 h-4" />
            {noCalendarsConnected ? 'Integrações' : 'Reconectar minha agenda'}
          </button>
        )}
      </div>
      )}
    </div>
  );
};
